import type { ReactNode } from 'react';
import { mioloSobre } from './cor';

/**
 * A grade de carimbos — a assinatura visual da cartela.
 *
 * Mora em `compartilhado/` porque aparece em TRÊS lugares, em dois builds
 * diferentes: a miniatura da tela Loja › Cartela, a fileira do Balcão, e a
 * grade grande no app da cliente. Três cópias divergiriam no primeiro ajuste,
 * e aí o que a lojista configura deixaria de ser o que a cliente vê.
 *
 * ⚠️ **O carimbo não é um círculo perfeito.** Ele é um blob levemente
 * irregular e torto, porque imita tinta apertada no papel — é o que faz a
 * cartela parecer o cartãozinho e não uma barra de progresso. A irregularidade
 * é DETERMINÍSTICA (tabela por índice, não aleatória): sorteio faria a mesma
 * cartela mudar de cara a cada render, e a cliente perceberia sem saber
 * explicar o quê.
 *
 * ⚠️ **Cor: `--accent`, nunca verde.** Carimbo dado é progresso, não "sucesso"
 * nem dinheiro — a regra de cor da casa. E `--accent` é a cor da LOJA, que a
 * lojista escolhe em Loja › Cartela.
 */

/**
 * A tabela da irregularidade — treze formas antes de repetir.
 *
 * Treze e não sete porque a cartela pode ter até 60 carimbos: com sete, o olho
 * pega a repetição em duas fileiras e a coisa volta a parecer gerada por
 * máquina, que é justamente o que o blob existe para evitar.
 */
const FORMAS = [
  { raio: '47% 53% 51% 49%', giro: -7, opacidade: 1 },
  { raio: '52% 48% 50% 50%', giro: 4, opacidade: 0.94 },
  { raio: '49% 51% 48% 52%', giro: -3, opacidade: 1 },
  { raio: '51% 49% 53% 47%', giro: 9, opacidade: 0.92 },
  { raio: '48% 52% 49% 51%', giro: -11, opacidade: 1 },
  { raio: '53% 47% 50% 50%', giro: 2, opacidade: 0.95 },
  { raio: '50% 50% 47% 53%', giro: -5, opacidade: 1 },
  { raio: '49% 51% 52% 48%', giro: 7, opacidade: 1 },
  { raio: '52% 48% 47% 53%', giro: -2, opacidade: 0.93 },
  { raio: '48% 52% 50% 50%', giro: 11, opacidade: 1 },
  { raio: '47% 53% 49% 51%', giro: -8, opacidade: 1 },
  { raio: '51% 49% 50% 50%', giro: 3, opacidade: 0.95 },
  { raio: '50% 50% 52% 48%', giro: -4, opacidade: 1 },
];

export function CartelaDesenhada({
  alvo,
  carimbos,
  cor,
  tamanho = 44,
  className,
}: {
  /** Quantos fecham a cartela. Define o tamanho da grade. */
  alvo: number;
  /** Quantos já foram dados. */
  carimbos: number;
  /**
   * A cor da loja. Quando ausente, herda o `--accent` do contexto — é assim
   * que o app pega a cor sem precisar passá-la em cada tela.
   */
  cor?: string;
  /** Lado do carimbo em px. 24 na miniatura do painel, 44 no app. */
  tamanho?: number;
  className?: string;
}): ReactNode {
  /*
   * Cinco por linha, sempre. Com 10 dá as duas fileiras do cartãozinho de
   * papel; com 20, quatro — e continua legível, que é a prova que o canvas
   * pediu. Mudar a contagem de colunas por tamanho faria a mesma loja ter
   * cartelas de cara diferente conforme o número escolhido.
   */
  return (
    <div
      className={className ? `cd-grade ${className}` : 'cd-grade'}
      role="img"
      aria-label={`${carimbos} de ${alvo} carimbos`}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(5, ${tamanho}px)`,
        gap: `${Math.round(tamanho * 0.22)}px ${Math.round(tamanho * 0.18)}px`,
        justifyContent: 'center',
        ...(cor ? ({ ['--accent' as string]: cor } as Record<string, string>) : {}),
      }}
    >
      {Array.from({ length: alvo }, (_, i) => {
        const dado = i < carimbos;
        /* O PRÓXIMO vazio é mais forte que os outros: é onde o olho da cliente
           precisa cair para entender "é aqui que entra o da próxima visita". */
        const proximo = i === carimbos;
        const forma = FORMAS[i % FORMAS.length]!;

        if (dado) {
          return (
            <span
              key={i}
              style={{
                width: tamanho,
                height: tamanho,
                background: 'var(--accent)',
                borderRadius: forma.raio,
                transform: `rotate(${forma.giro}deg)`,
                opacity: forma.opacidade,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/*
                O miolo — o anel claro por dentro da tinta, que é o que faz
                parecer carimbo de borracha e não bolinha. Só em tamanho grande:
                abaixo de 40px ele vira uma sujeira de 1px e piora o desenho.
              */}
              {tamanho >= 40 && (
                <span
                  style={{
                    width: Math.round(tamanho * 0.62),
                    height: Math.round(tamanho * 0.62),
                    borderRadius: '999px',
                    /* ⚠️ O anel acompanha a LUMINÂNCIA da cor: branco some
                       em tom claro, preto some em tom escuro. Sem isto, a
                       lojista escolhe um pastel e o carimbo vira mancha. */
                    border: `2px solid ${mioloSobre(cor ?? '#d4622a')}`,
                  }}
                />
              )}
            </span>
          );
        }

        return (
          <span
            key={i}
            style={{
              width: tamanho,
              height: tamanho,
              borderRadius: '999px',
              border: proximo
                ? '1.5px dashed var(--muted, #8b877f)'
                : '1px dashed var(--hair, rgba(20,18,15,.14))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              /* O número dentro do vazio conta a história sem texto: "esta é a
                 oitava visita". Só faz sentido no vazio — no cheio, a tinta
                 cobriria. */
              fontSize: Math.max(9, Math.round(tamanho * 0.34)),
              fontWeight: proximo ? 700 : 400,
              color: proximo ? 'var(--ink, #191a1e)' : 'var(--muted, #8b877f)',
              lineHeight: 1,
            }}
          >
            {i + 1}
          </span>
        );
      })}
    </div>
  );
}
